/**
 *
 * ExcusedStudents
 *
 */

import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// styles
import './style.scss';

// antd component
import { List, Empty } from 'antd';

// icons
import CustomIcons from 'components/CustomIcons';

// utils
import {
  isSameDates,
  getExcused,
  getExcusedAbsences,
  getAttendancesOnDate,
  generateAbsenceReason,
} from 'utils/helpers/helper.generateData';
import { getUniqueArrayObj, getObjId } from 'utils/helpers/helper.general';

function ExcusedStudents({
  dataSource,
  loading,
  course,
  onDate,
  // default props
  className,
  children,
}) {
  const childClassNames = classNames('excused-students', className);

  const [excusedStudents, setExcusedStudents] = useState([]);

  useEffect(() => {
    if (dataSource && onDate && course) {
      const excused = getExcused(dataSource, onDate, course);
      const excusedUnique = getUniqueArrayObj(excused, '_id');
      excused && setExcusedStudents(excusedUnique);
    }
  }, [dataSource, onDate, course]);

  const stdReason = useCallback((absData) => generateAbsenceReason(absData), []);

  const renderItem = useCallback((item) => {
    let abs;
    if (course?.attendances && course?.attendances.length > 0) {
      const currentAttendanceArr = getAttendancesOnDate(course, onDate);
      if (currentAttendanceArr.length > 0) {
        const absArr = getExcusedAbsences(
          currentAttendanceArr[0]?.missing_students,
          onDate,
          course,
        );
        const hasSickness = absArr?.filter(absence => absence?.status == 'sick') || [];

        abs = absArr.length > 0 && absArr[0];
        if (hasSickness?.length > 0) {
          abs = absArr.length > 0 && hasSickness[0];
        } 
      } else {
        const hasSickness = item?.hasAbs?.filter(absence => absence?.status == 'sick') || [];
        abs = item?.hasAbs && item?.hasAbs[0];
        if (hasSickness?.length > 0) {
          abs = item?.hasAbs && hasSickness[0];
        } 
      }
    } else {
      const hasSickness = item?.hasAbs?.filter(absence => absence?.status == 'sick') || [];
      abs = item?.hasAbs && item?.hasAbs[0];
      if (hasSickness?.length > 0) {
        abs = item?.hasAbs && hasSickness[0];
      } 
    }
    const reason = getObjId(item) == getObjId(abs?.student) && stdReason(abs);
    return (
      item && (
        <List.Item key={item?._id + '-excused'}>
          <CustomIcons type="person" />
          <List.Item.Meta
            title={`${item?.description?.lastname} ${item?.description?.firstname}`}
            description={reason}
          />
        </List.Item>
      )
    );
  }, [course, course?.attendances, onDate]);

  const renderList = useMemo(() => (
    <List
      className="excused-students__list"
      dataSource={excusedStudents || []}
      loading={loading}
      renderItem={renderItem}
      locale={{
        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun élève absent." />,
      }}
    />
  ), [excusedStudents, loading, renderItem]);

  return (
    <div className={childClassNames}>
      <h3 className="excused-students__title">Étudiant(s) excusé(s)</h3>
      {renderList}
      {children && <div className="excused-students__children">{children}</div>}
    </div>
  );
}

ExcusedStudents.defaultProps = {
  mode: 'simple',
};
ExcusedStudents.propTypes = {
  mode: PropTypes.oneOf(['simple', 'with-action']).isRequired,
  course: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
  ]),
  onDate: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.string.isRequired,
    PropTypes.object.isRequired
  ]),
  dataSource: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.array.isRequired
  ]),
};

export default memo(ExcusedStudents);
